import { createBrowserInspector } from '@statelyai/inspect';
import { createActorContext } from '@xstate/react';
import config from 'config';
import { signupStateChangeListener } from 'domains/signup/data/mixpanelTracking';
import { getAppointmentTypeFromURL, getCalendarIDFromURL } from 'domains/signup/data/queryParams';
import { signupMachine, SignupState } from 'domains/signup/signupFlow/signupMachine';
import { isArray } from 'lodash';
import { useEffectOnce } from 'react-use';

const shouldUseInspector = config.get('env') === 'development' && config.get('xstateDebugger') === 'true';
const inspect = shouldUseInspector ? createBrowserInspector().inspect : undefined;

export const SignupContext = createActorContext(signupMachine, {
  inspect,
});

type WithSignupContext = <P = Record<string, unknown>>(Component: React.FC<P>, fromScreener?: boolean) => React.FC<P>;

const appointmentType = getAppointmentTypeFromURL();
const calendarID = getCalendarIDFromURL();

/** Needs to be a component in order to access the nested context */
const MixpanelSubscriber = () => {
  const actorRef = SignupContext.useActorRef();

  useEffectOnce(() => {
    return actorRef.subscribe(({ value }) => {
      signupStateChangeListener(value as SignupState);
    }).unsubscribe;
  });

  return null;
};

export const withSignupProvider: WithSignupContext = (Component, fromScreener = false) => {
  return props => {
    return (
      <SignupContext.Provider
        options={{
          input: {
            appointmentType,
            calendarID,
            loadScreenerResults: fromScreener,
          },
        }}
      >
        <MixpanelSubscriber />
        <Component {...props} />
      </SignupContext.Provider>
    );
  };
};

export const withSignupMachineState = (Component: React.FC, matchingState: SignupState | SignupState[]) => {
  return (props: Record<string, unknown>) => {
    const matchesState = SignupContext.useSelector(state => {
      if (isArray(matchingState) && matchingState.some(s => state.matches(s))) {
        return true;
      }

      if (state.matches(matchingState as SignupState)) {
        return true;
      }

      return false;
    });

    if (matchesState) {
      return <Component {...props} />;
    }
    return null;
  };
};
